import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/axios';

interface GenericVideoResponse {
  generic_video: {
    slug: string;
    name: string;
    restaurant_name: string;
    restaurant_logo_url: string | null;
    active: boolean;
    available: boolean;
    portadilla_enabled: boolean;
  };
}

interface GenericMemoryResponse {
  slug: string;
  is_new: boolean;
}

export function GenericIdentifyPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: videoData, isLoading, error } = useQuery({
    queryKey: ['generic-video', slug],
    queryFn: async () => {
      const { data } = await api.get<GenericVideoResponse>(`/v1/generic_videos/${slug}`);
      return data.generic_video;
    },
    retry: false,
  });

  const createMemory = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<GenericMemoryResponse>('/v1/generic_memories', {
        slug,
        user: { name, email: email.toLowerCase(), locale: 'es' },
      });
      return data;
    },
    onSuccess: (data) => {
      navigate(`/memory/${data.slug}`, { replace: true });
    },
    onError: () => {
      toast.error('Error al crear el recuerdo. Inténtalo de nuevo.');
      setSubmitting(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('El email es obligatorio');
      return;
    }
    if (!termsAccepted) {
      toast.error('Debes aceptar los términos');
      return;
    }
    setSubmitting(true);
    createMemory.mutate();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full" />
      </div>
    );
  }

  if (error || !videoData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="text-center">
          <p className="text-lg font-medium text-gray-900">Enlace no disponible</p>
          <p className="text-sm text-gray-500 mt-2">Este enlace no está disponible en este momento</p>
        </div>
      </div>
    );
  }

  const video = videoData;

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-8">
        {video.restaurant_logo_url && (
          <div className="flex justify-center">
            <img src={video.restaurant_logo_url} alt={video.restaurant_name} className="h-16 object-contain" />
          </div>
        )}

        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{video.restaurant_name}</h1>
          <p className="text-gray-500 mt-1">{video.name}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Tu nombre
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="María García"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="maria@ejemplo.com"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="flex items-start gap-3">
            <input
              id="terms"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="terms" className="text-xs text-gray-500">
              Acepto recibir comunicaciones y autorizo el tratamiento de mis datos según la política de privacidad
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting || !email.trim() || !termsAccepted}
            className="w-full py-3 px-4 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Creando tu recuerdo...' : 'Crear mi recuerdo'}
          </button>
        </form>
      </div>
    </div>
  );
}
